import { yupResolver } from '@hookform/resolvers/yup';
// Removed import from @shared/documents
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
} from '../Shared/CommonStyles';
import {
	Button,
	FormContainer,
	ErrorMessage as FormErrorMessage,
	FormGroup,
	Input,
	Label,
} from '../Shared/FormStyles';

// Define the type locally
interface ComissioDocument {
	_id: string;
	name: string;
	description: string;
}

type ComissioFormData = Omit<ComissioDocument, '_id'>;

const comissioSchema = yup.object().shape({
	name: yup
		.string()
		.required('Aquest camp és obligatori')
		.min(2, 'El nom ha de tenir almenys 2 caràcters')
		.max(60, 'El nom no pot tenir més de 60 caràcters'),
	description: yup
		.string()
		.required('Aquest camp és obligatori')
		.min(2, 'La descripció ha de tenir almenys 2 caràcters')
		.max(200, 'La descripció no pot tenir més de 200 caràcters'),
});

const ComissioForm = ({ mode }: { mode: string }) => {
	const queryClient = useQueryClient();
	const { id } = useParams();
	const { user } = useAuth();

	const [authError, setAuthError] = useState<string | null>(null);

	const isAuthenticated =
		!!user &&
		(user.roles.includes('Admin') || user.roles.includes('Agrupament'));
	const isEditMode = mode === 'edit';

	const {
		data: comissio,
		isLoading: isFetchingComissio,
		error: fetchComissioError,
	} = useFetch<ComissioDocument>({
		queryKey: ['comissio', id],
		url: `/api/comissio/${id}`,
	});

	const putMutation = useSimpleMutation<ComissioFormData>({
		method: 'put',
		url: `/api/comissio/${id}`,
		dependencies: [id],
		navigateUrl: '/inici',
	});

	const postMutation = useSimpleMutation<ComissioFormData>({
		method: 'post',
		url: '/api/comissio',
		dependencies: [],
		navigateUrl: '/inici',
	});

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({ resolver: yupResolver(comissioSchema) });

	const invalidateQueries = () => {
		if (isEditMode) {
			queryClient.invalidateQueries({ queryKey: ['comissio', id] });
		}
		// NOTE: no ['comissions'] list query exists anywhere right now — nothing
		// else to invalidate on this surface.
	};

	const onSubmit = async (data: ComissioFormData) => {
		if (isEditMode) {
			putMutation.mutate(data, {
				onSuccess: () => {
					invalidateQueries();
				},
			});
		} else {
			postMutation.mutate(data, {
				onSuccess: () => {
					invalidateQueries();
				},
			});
		}
	};

	useEffect(() => {
		if (!user) {
			setAuthError('No estàs autenticat');
		} else if (!isAuthenticated) {
			setAuthError('No tens permís');
		} else {
			setAuthError(null);
		}
	}, [isAuthenticated]);

	useEffect(() => {
		if (comissio) {
			reset(comissio);
		}
	}, [reset, comissio]);

	const loading =
		isFetchingComissio || putMutation.isPending || postMutation.isPending;

	const error =
		authError ||
		fetchComissioError ||
		putMutation.error ||
		postMutation.error;

	return loading ? (
		<LoadingMessage>
			<LoadingGif size="large" />
		</LoadingMessage>
	) : error ? (
		<ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
	) : (
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			{/* <h2>{isEditMode ? 'Editar Comissió' : 'Crear Comissió'}</h2> */}

			<FormGroup>
				<Label htmlFor="name">Nom</Label>
				<Input id="name" type="text" {...register('name')} />
				{errors.name && (
					<FormErrorMessage>
						{errors.name.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="description">Descripció</Label>
				<Input
					id="description"
					type="text"
					{...register('description')}
				/>
				{errors.description && (
					<FormErrorMessage>
						{errors.description.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<Button type="submit" disabled={loading}>
				{loading
					? 'Guardant...'
					: isEditMode
						? 'Desar Canvis'
						: 'Crear Comissió'}
			</Button>
		</FormContainer>
	);
};

export default ComissioForm;
